import { invalidateInventory } from '../collection/inventoryInvalidation';
import { useEffect, useRef } from 'react';
import { InventoryAuthority } from '@qkindex/contracts';
import { Item } from '../../domain/item';
import { Location } from '../../domain/location';
import { itemService } from '../../services/itemService';
import { logger } from '../../services/logger';
import { useFeedback } from '../feedback/FeedbackProvider';
import { createOperationId } from '../../domain/operationId';

export function useItemActions({
  authority,
  deletionAuthority = authority,
  items,
  locations,
  keywords,
  location,
  qty,
  image,
  imageBlob,
  removeExistingImage,
  sourceItemId,
  tagIds,
  expectedRevision,
  resetForm,
  setLastUsedLocation,
  setSelectedItem,
  setItems,
  closeAdd,
  closeDetails,
  onCapacityChanged,
  onCreated,
  onUpdated,
}: {
  authority?: InventoryAuthority;
  deletionAuthority?: InventoryAuthority;
  items: Item[];
  locations: Location[];
  keywords: string;
  location: string;
  qty: number | string;
  image: string | null;
  imageBlob: Blob | null;
  removeExistingImage: boolean;
  sourceItemId?: string;
  tagIds?: string[];
  expectedRevision?: number;
  resetForm: (nextLocation?: string) => void;
  setLastUsedLocation: (location: string) => Promise<void> | void;
  setSelectedItem: (item: Item | null) => void;
  setItems: React.Dispatch<React.SetStateAction<Item[]>>;
  closeAdd: () => void;
  closeDetails: () => void;
  onCapacityChanged?: () => void;
  onCreated?: (itemId: string) => void;
  onUpdated?: (fields: Partial<Item>) => void;
}) {
  const { notify } = useFeedback();
  const tagKey = JSON.stringify(tagIds);
  const pendingCreateOperationId = useRef<string | undefined>(undefined);
  useEffect(() => {
    pendingCreateOperationId.current = undefined;
  }, [
    keywords,
    location,
    qty,
    image,
    imageBlob,
    sourceItemId,
    tagKey,
    authority?.collectionId,
    authority?.userId,
  ]);
  const pendingReplace = useRef<
    { itemId: string; operationId: string } | undefined
  >(undefined);
  useEffect(() => {
    pendingReplace.current = undefined;
  }, [
    keywords,
    location,
    qty,
    imageBlob,
    tagKey,
    expectedRevision,
    authority?.collectionId,
    authority?.userId,
  ]);
  const numericQty = () =>
    typeof qty === 'string' ? Number.parseFloat(qty) || 0 : qty;
  const create = async () => {
    if (!authority || !location.trim()) return false;
    const operationId = pendingCreateOperationId.current || createOperationId();
    pendingCreateOperationId.current = operationId;
    const itemId = await itemService.createItem(
      authority,
      {
        keywords,
        ...(tagIds === undefined ? {} : { tagIds }),
        location,
        locationId: locations.find((candidate) => candidate.name === location)
          ?.id,
        qty: numericQty(),
        image: imageBlob,
        sourceItemId: !imageBlob ? sourceItemId : undefined,
      },
      operationId,
    );
    pendingCreateOperationId.current = undefined;
    invalidateInventory({
      ...authority,
      resources: ['items'],
      reloadItemIds: [itemId],
    });
    onCreated?.(itemId);
    onCapacityChanged?.();
    await setLastUsedLocation(location);
    return true;
  };
  const report = async (
    action: () => Promise<void | boolean>,
    message: string,
    capacitySensitive = false,
  ): Promise<boolean> => {
    try {
      return (await action()) !== false;
    } catch (error) {
      logger.error(message, error);
      const cause = (error as { cause?: unknown })?.cause;
      const contractCode =
        (error as { details?: { code?: unknown } })?.details?.code ||
        (cause as { details?: { code?: unknown } })?.details?.code;
      if (capacitySensitive && contractCode === 'capacity-exceeded') {
        onCapacityChanged?.();
        notify(
          'This collection no longer has an item unit available. Your draft is still open.',
          'warning',
        );
        return false;
      }
      if (contractCode === 'operation-conflict') {
        notify(
          'This item changed. Reopen it before saving your changes.',
          'warning',
        );
        return false;
      }
      notify(
        (error as { code?: string })?.code === 'mutation-pending'
          ? (error as Error).message
          : message,
        'error',
      );
      return false;
    }
  };

  return {
    saveAndAddAnother: () =>
      report(
        async () => {
          if (!(await create())) return false;
          resetForm(location);
          return true;
        },
        'The item could not be saved.',
        true,
      ),
    saveAndClose: () =>
      report(
        async () => {
          if (!(await create())) return false;
          resetForm();
          closeAdd();
          return true;
        },
        'The item could not be saved.',
        true,
      ),
    update: (itemId: string) =>
      report(async () => {
        if (
          !authority ||
          !location.trim() ||
          !locations.some((candidate) => candidate.name === location)
        )
          return false;
        const fields = {
          keywords,
          location,
          locationId: locations.find((candidate) => candidate.name === location)
            ?.id,
          qty: numericQty(),
          ...(tagIds === undefined ? {} : { tagIds }),
          ...(expectedRevision === undefined ? {} : { expectedRevision }),
        };
        if (imageBlob && !removeExistingImage) {
          const operationId =
            pendingReplace.current?.itemId === itemId
              ? pendingReplace.current.operationId
              : createOperationId();
          pendingReplace.current = { itemId, operationId };
          await itemService.replaceImage(
            authority,
            itemId,
            imageBlob,
            fields,
            operationId,
          );
          pendingReplace.current = undefined;
        } else {
          await itemService.updateItem(authority, itemId, {
            ...fields,
            ...(removeExistingImage ? { image: '', storagePath: '' } : {}),
          });
        }
        const { expectedRevision: _expectedRevision, ...savedFields } = fields;
        const original = items.find((item) => item.id === itemId);
        const changed =
          imageBlob ||
          removeExistingImage ||
          Object.entries(savedFields).some(
            ([key, value]) =>
              JSON.stringify(value) !==
              JSON.stringify(original?.[key as keyof Item]),
          );
        onUpdated?.({
          ...savedFields,
          ...(expectedRevision !== undefined && changed
            ? { revision: expectedRevision + 1 }
            : {}),
          ...(removeExistingImage ? { image: '', storagePath: '' } : {}),
        });
        invalidateInventory({
          ...authority,
          resources: ['items'],
          reloadItemIds: [itemId],
        });
        await setLastUsedLocation(location);
        setSelectedItem(null);
        closeDetails();
        resetForm();
        return true;
      }, 'The item could not be updated.'),
    moveToBin: (itemId: string) =>
      report(async () => {
        if (!deletionAuthority || !items.some((item) => item.id === itemId))
          return false;
        await itemService.moveItemToBin(deletionAuthority, itemId);
        invalidateInventory({
          ...deletionAuthority,
          resources: ['items'],
          reloadItemIds: [itemId],
        });
        setItems((current) => current.filter((item) => item.id !== itemId));
        return true;
      }, 'The item could not be moved to the bin.'),
  };
}
